import { isAxiosError } from 'axios';
import { client } from '../common/client';
import type { GetProductReviewInsightRequest, GetProductReviewInsightResponse } from './utils/types';
import { convertReviewInsightToWhatPeopleSayInsight } from './utils/converter';
import type { WhatPeopleSayInsight } from '../../model/whatPeopleSay/whatPeopleSay';

/**
 * What People Say — cross-site review aggregation (SSD-5362).
 * Backend: `GET /reviews/product/:productId/insight?locale=<en|ko>` (SkinSeoulFacade, dev).
 * Returns a `{ reviewInsight }` wrapper — `null` when no publishable insight exists (the converter
 * maps that to the "gathering reviews" card). Demo product 131326 returns a ready insight on `ko`.
 */
class WhatPeopleSayAPIClient {
  public async getWhatPeopleSay({ productId, locale }: GetProductReviewInsightRequest): Promise<WhatPeopleSayInsight> {
    try {
      const response = await client
        .get<GetProductReviewInsightResponse>(`/reviews/product/${productId}/insight`, {
          params: locale ? { locale } : undefined,
        })
        .then((res) => res.data);

      return convertReviewInsightToWhatPeopleSayInsight(response);
    } catch (error) {
      // The backend returns 200 + `{ reviewInsight: null }` for a no-data product (→ gathering card).
      //
      // 404 — the endpoint is absent for this environment/product, a real no-section case.
      // 400 — the request carried a language this endpoint does not serve. We forward the user's
      //       language setting verbatim and its picker offers more languages than the endpoint
      //       does, so this is an expected, user-reachable state, not a defect: the section is
      //       simply unavailable in that language.
      //
      // Both map to `not_mapped` (renders nothing) so an unsupported language degrades to a
      // missing section instead of throwing into the PDP's error boundary — which would replace
      // the whole route segment — and without react-query retries or per-PDP Sentry noise.
      const status = isAxiosError(error) ? error.response?.status : undefined;

      if (status === 404 || status === 400) {
        return { availability: 'not_mapped', data: null };
      }
      throw error;
    }
  }
}

export const whatPeopleSayAPIClient = new WhatPeopleSayAPIClient();
